import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { DependencyFilterKeys } from '../../../../../../shared/dependenciesV2';
import { useOrganizationPackageLabels } from '../../../../../hooks/api/packageLabels';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import { selectOptionFactory } from '../../../../../utils';
import FilterCardMultiSelect from '../../../../generic/Filter/FilterCardMultiSelect';
import useFetchDependenciesAction from '../../hooks/useFetchDependencyAction';
import type { SelectOption } from '../../../../../types';

/**
 * Package labels filter which allows the user to filter dependencies by package labels.
 * Uses the useOrganizationPackageLabels hook to obtain a list of available labels.
 */
const PackageLabelsFilter: VoidFunctionComponent = () => {
  const filters = useSelector(DependenciesV2Store.selectors.getFilters);
  const setFilter = useFetchDependenciesAction(DependenciesV2Store.actions.setFilters);

  // Load org package labels with the useOrganizationPackageLabels hook.
  const { data: labelsResponse, isLoading: areLabelsLoading } = useOrganizationPackageLabels();
  const labels = labelsResponse?.packageLabels ?? [];
  const isDisabled = labels.length === 0;

  // Convert the available and selected labels to SelectOptions.
  const availableLabels = labels.map(
    (label) => selectOptionFactory(label.name),
  );
  const selectedLabels = (filters.packageLabels ?? []).map(
    (label) => selectOptionFactory(label),
  );

  // When the label selection changes, update the DepsV2 filters.
  const onChange = (selected: SelectOption[]) => setFilter({
    ...filters,
    [DependencyFilterKeys.PackageLabels]: selected.map((option) => option.value),
  });

  return (
    <FilterCardMultiSelect
      label="Package Labels"
      placeholder={areLabelsLoading ? 'Loading...' : 'Select one or more...'}
      options={availableLabels}
      values={selectedLabels}
      onChange={onChange}
      isDisabled={isDisabled}
    />
  );
};

export default PackageLabelsFilter;
